import { getUserId } from '../userLocalDetails';

// Persisted "My Drafts" preferences (per user), so that navigating away and
// back keeps the user on their last-viewed draft and keeps the subjects filter
// on its previously selected setting.

const LAST_DRAFT_PREFIX = 'myDrafts.lastDraftId';
const SUBJECT_FILTER_PREFIX = 'myDrafts.subjectFilter';

const scopedKey = (prefix) => {
  let userId;
  try {
    userId = getUserId();
  } catch {
    userId = null;
  }
  return `${prefix}.${userId ?? 'anon'}`;
};

const readRaw = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeRaw = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage unavailable / quota errors - persistence is best-effort.
  }
};

// --- Last viewed draft ---

export const getLastViewedDraftId = () => {
  const raw = readRaw(scopedKey(LAST_DRAFT_PREFIX));
  return raw ? raw : null;
};

export const setLastViewedDraftId = (id) => {
  if (!id) return;
  writeRaw(scopedKey(LAST_DRAFT_PREFIX), String(id));
};

// --- Subjects filter ---
// Tri-state: absent => never chosen (fall back to following the open draft),
// null => user explicitly chose "All Subjects", string => a specific subject.
// A valid stored preference is only `null` or a string; anything else (corrupt
// or tampered storage) is treated as "never chosen" so auto-follow keeps working.

const isValidSubjectFilter = (value) =>
  value === null || typeof value === 'string';

const readStoredSubjectFilter = () => {
  const raw = readRaw(scopedKey(SUBJECT_FILTER_PREFIX));
  if (raw === null) return { present: false, value: null };
  try {
    const parsed = JSON.parse(raw);
    if (isValidSubjectFilter(parsed)) return { present: true, value: parsed };
  } catch {
    // fall through - corrupt value is treated as "not set"
  }
  return { present: false, value: null };
};

export const hasStoredSubjectFilter = () => readStoredSubjectFilter().present;

export const getStoredSubjectFilter = () => readStoredSubjectFilter().value;

export const setStoredSubjectFilter = (value) => {
  const normalized = typeof value === 'string' ? value : null;
  writeRaw(scopedKey(SUBJECT_FILTER_PREFIX), JSON.stringify(normalized));
};
